import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useT } from '../context/I18nContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { CreditCard, Bitcoin, Wallet, Loader2, Truck, Info, LogIn } from 'lucide-react';
import { api } from '../api/client';
import Turnstile from '../components/Turnstile';
import { useSite } from '../context/SiteContext';

const FIAT_METHODS = ['ideal', 'card', 'bancontact', 'paypal'];

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { t } = useT();
  const { user: customerUser, loading: authLoading } = useCustomerAuth();
  const { site } = useSite();
  const paymentStepEnabled = !!site?.checkout?.payment_step_enabled;
  const requireLogin = site?.checkout?.require_customer_login !== false;
  const [method, setMethod] = useState('crypto');
  const [cryptoCoin, setCryptoCoin] = useState('BTC');
  const [submitting, setSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [zones, setZones] = useState([]);
  const [defaultPrice, setDefaultPrice] = useState(15);
  const [shipQuote, setShipQuote] = useState({ price: 0, eta: '', free: false, zone_name: '' });
  const [tsConfig, setTsConfig] = useState({ enabled: false, siteKey: '' });
  const [tsToken, setTsToken] = useState('');
  const [form, setForm] = useState({
    email: '', phone: '', first_name: '', last_name: '', address: '', city: '', postal_code: '', country: 'NL',
  });
  const nav = useNavigate();

  useEffect(() => {
    api.shippingZones().then((r) => { setZones(r.zones || []); setDefaultPrice(r.default_price || 15); }).catch(() => {});
    api.securityPublic().then((r) => setTsConfig({ enabled: r.turnstile_enabled, siteKey: r.turnstile_site_key })).catch(() => {});
  }, []);

  // Pre-fill from logged-in customer profile
  useEffect(() => {
    if (customerUser) {
      setForm((f) => ({
        ...f,
        email: f.email || customerUser.email || '',
        phone: f.phone || customerUser.phone || '',
        first_name: f.first_name || (customerUser.name || '').split(' ')[0] || '',
        last_name: f.last_name || (customerUser.name || '').split(' ').slice(1).join(' ') || '',
      }));
    }
  }, [customerUser]);

  const discount = coupon ? coupon.discount || 0 : 0;
  const postDiscountSubtotal = useMemo(() => Math.max(0, subtotal - discount), [subtotal, discount]);

  // Refresh shipping quote when country or post-discount subtotal changes
  useEffect(() => {
    if (!form.country) return;
    api.shippingQuote(form.country, postDiscountSubtotal)
      .then(setShipQuote)
      .catch(() => setShipQuote({ price: defaultPrice, eta: '', free: false, zone_name: form.country }));
  }, [form.country, postDiscountSubtotal, defaultPrice]);

  const shippingCost = shipQuote?.price || 0;
  const total = Math.max(0, postDiscountSubtotal + shippingCost);

  const applyCoupon = async () => {
    if (!couponInput) return;
    setCouponLoading(true);
    try {
      const res = await api.validateCoupon(couponInput, subtotal);
      setCoupon(res);
      toast.success(`Code ${res.code} applied: -€${res.discount.toFixed(2)}`);
    } catch (e) {
      setCoupon(null);
      toast.error(e?.response?.data?.detail || 'Invalid code');
    } finally { setCouponLoading(false); }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error(t('checkout.empty_cart'));
    if (requireLogin && !customerUser) {
      toast.error(t('account.required_notice'));
      nav('/login?return=/checkout');
      return;
    }
    if (!form.phone || !form.phone.trim()) return toast.error(t('checkout.phone_required'));
    if (tsConfig.enabled && tsConfig.siteKey && !tsToken) return toast.error('Please complete the security check');
    setSubmitting(true);
    try {
      const order = await api.createOrder({
        email: form.email,
        items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })),
        shipping: {
          first_name: form.first_name, last_name: form.last_name, address: form.address,
          city: form.city, postal_code: form.postal_code, country: form.country, phone: form.phone,
        },
        payment_method: paymentStepEnabled ? method : 'contact',
        crypto_coin: paymentStepEnabled && method === 'crypto' ? cryptoCoin : null,
        coupon_code: coupon?.code || null,
        turnstile_token: tsToken || null,
      });
      clear();

      // Route based on payment step & method
      if (!paymentStepEnabled) {
        toast.success('Order placed — we will contact you shortly');
        nav(`/order/${order.order_number}/thanks`);
        return;
      }
      if (method === 'crypto') {
        toast.success('Order placed — send crypto to complete');
        nav(`/order/${order.order_number}/crypto`);
        return;
      }
      if (FIAT_METHODS.includes(method)) {
        toast.success('Order placed — buy your voucher to complete');
        nav(`/order/${order.order_number}/voucher`);
        return;
      }
      if (order.payment_url && /^https?:/i.test(order.payment_url)) {
        toast.success('Redirecting to payment…');
        window.location.href = order.payment_url;
      } else {
        toast.success(`Order ${order.order_number} placed!`);
        setTimeout(() => nav(`/order/${order.order_number}/thanks`), 1200);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (err?.response?.status === 401) {
        toast.error(detail || t('account.required_notice'));
        nav('/login?return=/checkout');
        return;
      }
      toast.error(detail || 'Could not place order');
    } finally {
      setSubmitting(false);
    }
  };

  // Auth wall: if login required and not logged in, show a friendly prompt instead of the form
  if (!authLoading && requireLogin && !customerUser) {
    return (
      <>
        <Header />
        <main className="bg-black min-h-[60vh]">
          <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="checkout-auth-wall">
            <LogIn className="h-10 w-10 mx-auto mb-4 text-[#d4af37]" />
            <h1 className="serif-italic gold-gradient-text text-4xl mb-3">{t('checkout.title')}</h1>
            <p className="text-zinc-400 mb-6">{t('account.required_notice')}</p>
            <div className="flex gap-3 justify-center">
              <Link to="/login?return=/checkout"><Button className="btn-gold h-11 px-6" data-testid="checkout-goto-login">{t('account.sign_in')}</Button></Link>
              <Link to="/register?return=/checkout"><Button variant="outline" className="btn-outline-gold h-11 px-6" data-testid="checkout-goto-register">{t('account.create_account')}</Button></Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="bg-black min-h-[60vh]">
        <div className="max-w-[1200px] mx-auto px-6 py-16">
          <h1 className="serif-italic gold-gradient-text text-5xl">{t('checkout.title')}</h1>

          <form onSubmit={submit} className="mt-10 grid lg:grid-cols-[1fr,360px] gap-10">
            <div className="space-y-8">
              <section className="border border-zinc-900 rounded p-6">
                <h3 className="serif-italic text-2xl text-[#d4af37] mb-4">{t('checkout.contact_shipping')}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">{t('checkout.email')}</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-transparent border-zinc-800 mt-1" data-testid="checkout-email" />
                  </div>
                  <div>
                    <Label className="text-zinc-300">{t('checkout.phone')} <span className="text-rose-400">*</span></Label>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-transparent border-zinc-800 mt-1" placeholder="+31 6 12 34 56 78" data-testid="checkout-phone" />
                    <p className="text-zinc-500 text-xs mt-1">{t('checkout.phone_help')}</p>
                  </div>
                  <div><Label className="text-zinc-300">{t('checkout.first_name')}</Label><Input required value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="bg-transparent border-zinc-800 mt-1" data-testid="checkout-firstname" /></div>
                  <div><Label className="text-zinc-300">{t('checkout.last_name')}</Label><Input required value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="bg-transparent border-zinc-800 mt-1" data-testid="checkout-lastname" /></div>
                  <div className="md:col-span-2"><Label className="text-zinc-300">{t('checkout.address')}</Label><Input required value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="bg-transparent border-zinc-800 mt-1" data-testid="checkout-address" /></div>
                  <div><Label className="text-zinc-300">{t('checkout.city')}</Label><Input required value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="bg-transparent border-zinc-800 mt-1" data-testid="checkout-city" /></div>
                  <div><Label className="text-zinc-300">{t('checkout.postal')}</Label><Input required value={form.postal_code} onChange={(e) => setForm({...form, postal_code: e.target.value})} className="bg-transparent border-zinc-800 mt-1" data-testid="checkout-postal" /></div>
                  <div className="md:col-span-2">
                    <Label className="text-zinc-300">{t('checkout.country')}</Label>
                    <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                      <SelectTrigger className="bg-transparent border-zinc-800 mt-1" data-testid="checkout-country"><SelectValue placeholder="Select a country" /></SelectTrigger>
                      <SelectContent>
                        {zones.map((z) => (
                          <SelectItem key={z.country_code} value={z.country_code}>
                            {z.country_name} — €{z.price.toFixed(2)}{z.free_over > 0 ? ` (${t('checkout.free').toLowerCase()} > €${z.free_over.toFixed(0)})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {shipQuote?.zone_name && (
                      <p className="text-zinc-500 text-xs mt-2 flex items-center gap-2">
                        <Truck className="h-3.5 w-3.5 text-[#d4af37]" />
                        {shipQuote.free ? <span className="text-emerald-400">{t('checkout.free_unlocked')}</span> : <>{t('checkout.shipping_to')} {shipQuote.zone_name}: <span className="text-[#d4af37]">€{shippingCost.toFixed(2)}</span></>}
                        {shipQuote.eta && <span className="text-zinc-600">· {shipQuote.eta}</span>}
                      </p>
                    )}
                  </div>
                </div>
              </section>

              {paymentStepEnabled && (
              <section className="border border-zinc-900 rounded p-6">
                <h3 className="serif-italic text-2xl text-[#d4af37] mb-2">{t('checkout.payment_method')}</h3>
                <div className="flex items-start gap-2 bg-[#d4af37]/5 border border-[#d4af37]/20 text-zinc-300 text-xs rounded p-3 mb-4">
                  <Info className="h-4 w-4 text-[#d4af37] shrink-0 mt-0.5" />
                  <p>{t('checkout.payment_info')}</p>
                </div>
                <RadioGroup value={method} onValueChange={setMethod} className="grid md:grid-cols-2 gap-3">
                  {[
                    { id: 'crypto', label: 'Crypto (direct) — 15% off', desc: 'BTC / ETH / LTC / USDT — pay directly, receive discount', Icon: Bitcoin },
                    { id: 'ideal', label: 'iDEAL', desc: 'Redirects to voucher provider (Bitrefill)', Icon: Wallet },
                    { id: 'card', label: 'Credit/Debit Card', desc: 'Redirects to voucher provider (Bitrefill)', Icon: CreditCard },
                    { id: 'bancontact', label: 'Bancontact', desc: 'Redirects to voucher provider (Bitrefill)', Icon: Wallet },
                    { id: 'paypal', label: 'PayPal', desc: 'Redirects to voucher provider (Azteco)', Icon: Wallet },
                  ].map((m) => (
                    <label key={m.id} data-testid={`pay-${m.id}`} className={`border rounded p-4 cursor-pointer flex items-start gap-3 transition-colors ${method===m.id?'border-[#d4af37] bg-[#d4af37]/5':'border-zinc-800 hover:border-zinc-700'}`}>
                      <RadioGroupItem value={m.id} id={m.id} className="mt-1" />
                      <div>
                        <div className="flex items-center gap-2 text-zinc-100"><m.Icon className="h-4 w-4 text-[#d4af37]" /> {m.label}</div>
                        <p className="text-xs text-zinc-500 mt-1">{m.desc}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
                {method === 'crypto' && (
                  <div className="mt-4 border-t border-zinc-900 pt-4" data-testid="crypto-coin-picker">
                    <p className="text-zinc-300 text-sm mb-2">Choose your coin (15% discount applied):</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {['BTC', 'ETH', 'LTC', 'USDT'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCryptoCoin(c)}
                          data-testid={`coin-${c}`}
                          className={`border rounded p-3 text-sm font-mono uppercase transition-colors ${cryptoCoin === c ? 'border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]' : 'border-zinc-800 text-zinc-300 hover:border-zinc-700'}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </section>
              )}
              {!paymentStepEnabled && (
                <section className="border border-[#d4af37]/30 bg-[#d4af37]/5 rounded p-6" data-testid="checkout-contact-notice">
                  <h3 className="serif-italic text-2xl text-[#d4af37] mb-2">{t('thanks.eyebrow')}</h3>
                  <p className="text-zinc-300 text-sm">
                    After you place your order, our team will contact you on the phone/email you provided to arrange the easiest payment method for you.
                  </p>
                </section>
              )}
            </div>

            <aside className="border border-zinc-900 rounded p-6 h-fit bg-zinc-950">
              <h3 className="serif-italic text-2xl text-[#d4af37] mb-4">{t('checkout.your_order')}</h3>
              <ul className="space-y-2 max-h-[260px] overflow-auto pr-2">
                {items.map((i) => (
                  <li key={i.id} className="flex justify-between text-sm text-zinc-300"><span>{i.name} × {i.qty}</span><span>€{(i.price*i.qty).toFixed(2)}</span></li>
                ))}
                {items.length === 0 && <li className="text-zinc-500 text-sm">{t('checkout.no_items')} <Link to="/shop" className="text-[#d4af37] underline">{t('checkout.add_some')}</Link></li>}
              </ul>

              {/* Coupon */}
              <div className="mt-4 pt-4 border-t border-zinc-800">
                {!coupon ? (
                  <div className="flex gap-2">
                    <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder={t('checkout.promo_code')} className="bg-transparent border-zinc-800 text-sm uppercase font-mono" data-testid="coupon-input" />
                    <Button type="button" disabled={couponLoading} onClick={applyCoupon} variant="outline" className="btn-outline-gold shrink-0 h-9 px-3 text-xs" data-testid="coupon-apply">{t('checkout.apply')}</Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-600/40 rounded px-3 py-2 text-sm">
                    <span className="text-emerald-400"><span className="font-mono">{coupon.code}</span> · −€{discount.toFixed(2)}</span>
                    <button type="button" onClick={removeCoupon} className="text-zinc-400 hover:text-rose-400 text-xs">{t('checkout.remove_coupon')}</button>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-zinc-800 space-y-1.5 text-sm">
                <div className="flex justify-between text-zinc-400"><span>{t('checkout.subtotal')}</span><span data-testid="summary-subtotal">€{subtotal.toFixed(2)}</span></div>
                {discount > 0 && <div className="flex justify-between text-emerald-400"><span>{t('checkout.discount')}</span><span>−€{discount.toFixed(2)}</span></div>}
                <div className="flex justify-between text-zinc-400">
                  <span>{t('checkout.shipping')} {shipQuote?.zone_name ? `(${shipQuote.zone_name})` : ''}</span>
                  <span data-testid="summary-shipping">{shippingCost > 0 ? `€${shippingCost.toFixed(2)}` : <span className="text-emerald-400">{t('checkout.free')}</span>}</span>
                </div>
                <div className="flex justify-between text-zinc-100 pt-1 border-t border-zinc-800 mt-2"><span className="text-base">{t('checkout.total')}</span><span className="text-[#d4af37] text-lg" data-testid="summary-total">€{total.toFixed(2)}</span></div>
              </div>

              <Button type="submit" disabled={submitting} data-testid="checkout-submit" className="btn-gold w-full mt-6 h-12 tracking-wider uppercase">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('checkout.processing')}</> : t('checkout.place_order')}
              </Button>
              {tsConfig.enabled && tsConfig.siteKey && (
                <div className="mt-4 flex justify-center">
                  <Turnstile siteKey={tsConfig.siteKey} onToken={setTsToken} />
                </div>
              )}
            </aside>
          </form>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Checkout;
